import { useEffect, useMemo, useState } from "react";
import { alertKind, isProminent, sortAlerts } from "../../shared/alerts";
import { resortById } from "../../shared/mountains";
import { useMountains } from "../data/newsQueries";
import { useAlerts } from "../data/weatherQueries";
import { Icon, type IconName } from "../ui/Icon";
import { KIND_ICON, toneOf, untilText } from "../widgets/alerts/alertUi";
import { affectsCars } from "../../shared/drive";
import { useAppUi } from "./uiContext";
import { useHousehold } from "./useHousehold";

interface Item {
  key: string;
  tone: string;
  icon: IconName;
  text: string;
  detail: string | null;
  /** Starter screen to open on tap (shared/presets.ts). */
  screen: string;
}

const ROTATE_MS = 8000;

/**
 * Heads up, on Home and Morning: what's worth knowing before heading out that
 * isn't loud enough for the red bar on every screen. Watches and advisories,
 * a powder day at the family's mountains, and chain/traction laws or closures
 * on the way there. Shows only when there is something; tap for the screen
 * that has the details.
 */
export function HeadsUp() {
  const { timeZone, settings } = useHousehold();
  const { openScreen } = useAppUi();
  const alerts = useAlerts(true);
  const mountains = useMountains();

  const items = useMemo(() => {
    const out: Item[] = [];
    for (const a of sortAlerts(alerts.data?.alerts ?? []).filter((x) => !isProminent(x))) {
      out.push({ key: `wx:${a.id}`, tone: `tone-${toneOf(a)}`, icon: KIND_ICON[alertKind(a.event)], text: a.event, detail: untilText(a, timeZone, settings.clock24h), screen: "alerts" });
    }
    const m = mountains.data;
    if (m) {
      for (const r of m.resorts.filter((x) => x.powder)) {
        const amount = r.powder === "today" ? `${r.newSnow24In}″ new` : `${r.forecast24In}″ forecast`;
        out.push({ key: `powder:${r.id}`, tone: "tone-powder", icon: "snow", text: `${r.powder === "today" ? "Powder day" : "Powder tomorrow"} at ${r.name}`, detail: amount, screen: "outdoors" });
      }
      const roads = m.roads.filter(affectsCars);
      for (const e of roads.filter((x) => x.kind === "closure")) {
        out.push({ key: `road:${e.id}`, tone: "tone-warning", icon: "car", text: `${e.route} closed`, detail: e.where, screen: "outdoors" });
      }
      const laws = roads.filter((x) => x.kind !== "closure");
      if (laws.length) {
        const chain = laws.some((x) => x.kind === "chain");
        const routes = [...new Set(laws.map((x) => x.route))];
        const to = [...new Set(laws.flatMap((x) => x.resorts))].map((id) => resortById(id)?.name).filter(Boolean);
        out.push({ key: `laws:${routes.join()}`, tone: "tone-watch", icon: "car", text: `${chain ? "Chain law" : "Traction law"}: ${routes.join(", ")}`, detail: to.length ? `on the way to ${to.join(", ")}` : null, screen: "outdoors" });
      }
    }
    return out;
  }, [alerts.data, mountains.data, timeZone, settings.clock24h]);

  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const t = window.setInterval(() => setI((n) => n + 1), ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  const item = items[i % Math.max(1, items.length)];
  if (!item) return null;
  return (
    <button className={`alert-bar heads-up ${item.tone}`} onClick={() => openScreen(item.screen)} aria-live="polite">
      <Icon name={item.icon} size="1.25rem" />
      <strong key={item.key} className="heads-up-text">
        {item.text}
      </strong>
      {item.detail && <span className="heads-up-detail">{item.detail}</span>}
      {items.length > 1 && (
        <span className="alert-bar-more">
          {(i % items.length) + 1} of {items.length}
        </span>
      )}
      <span className="alert-bar-cta">
        Details <Icon name="chevronRight" size="1rem" />
      </span>
    </button>
  );
}
